import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { CONVERSION_LOCALES } from "@/locales/conversion";

const COPY = {
  en: {
    security: "Account security",
    forgotTitle: "Reset your password", forgotBody: "Enter your account email. If it exists, we will send a secure reset link.",
    verifyTitle: "Verify your email", verifyBody: "Confirm the email address for your TradeDocs account or request a new link.",
    resetTitle: "Choose a new password", email: "Email", password: "New password", confirm: "Confirm password",
    send: "Send secure link", verify: "Verify email", resend: "Send verification link", reset: "Save new password",
    sent: "If the account exists, a secure email has been sent.", verified: "Email verified. You can now sign in.",
    resetDone: "Password updated. Sign in with your new password.", back: "Back to sign in",
    mismatch: "Passwords do not match.", invalid: "This link is invalid or has expired.", working: "Working…",
  },
  it: {
    forgotTitle: "Reimposta la password", forgotBody: "Inserisci l’email dell’account. Se esiste, invieremo un link sicuro.",
    verifyTitle: "Verifica la tua email", verifyBody: "Conferma l’indirizzo email dell’account TradeDocs o richiedi un nuovo link.",
    resetTitle: "Scegli una nuova password", email: "Email", password: "Nuova password", confirm: "Conferma password",
    send: "Invia link sicuro", verify: "Verifica email", resend: "Invia link di verifica", reset: "Salva nuova password",
    sent: "Se l’account esiste, è stata inviata un’email sicura.", verified: "Email verificata. Ora puoi accedere.",
    resetDone: "Password aggiornata. Accedi con la nuova password.", back: "Torna all’accesso",
    mismatch: "Le password non coincidono.", invalid: "Il link non è valido o è scaduto.", working: "Operazione in corso…",
  },
  de: {
    forgotTitle: "Passwort zurücksetzen", forgotBody: "Geben Sie Ihre Konto-E-Mail ein. Falls das Konto existiert, senden wir einen sicheren Link.",
    verifyTitle: "E-Mail bestätigen", verifyBody: "Bestätigen Sie die E-Mail-Adresse Ihres TradeDocs-Kontos oder fordern Sie einen neuen Link an.",
    resetTitle: "Neues Passwort wählen", email: "E-Mail", password: "Neues Passwort", confirm: "Passwort bestätigen",
    send: "Sicheren Link senden", verify: "E-Mail bestätigen", resend: "Bestätigungslink senden", reset: "Neues Passwort speichern",
    sent: "Falls das Konto existiert, wurde eine sichere E-Mail versendet.", verified: "E-Mail bestätigt. Sie können sich jetzt anmelden.",
    resetDone: "Passwort aktualisiert. Melden Sie sich mit dem neuen Passwort an.", back: "Zurück zur Anmeldung",
    mismatch: "Die Passwörter stimmen nicht überein.", invalid: "Der Link ist ungültig oder abgelaufen.", working: "Wird verarbeitet…",
  },
};

export default function AuthRecoveryPage({ mode }) {
  const { lang } = useI18n();
  const c = CONVERSION_LOCALES[lang]?.authRecovery || COPY[lang] || COPY.en;
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const token = params.get("token") || "";

  const submit = async (event) => {
    event.preventDefault(); setLoading(true); setError(""); setMessage("");
    try {
      if (mode === "forgot") {
        await api.forgotPassword({ email }); setMessage(c.sent);
      } else if (mode === "verify" && token) {
        await api.confirmEmailVerification({ token }); setMessage(c.verified);
      } else if (mode === "verify") {
        await api.requestEmailVerification({ email }); setMessage(c.sent);
      } else {
        if (password !== confirm) { setError(c.mismatch); return; }
        await api.resetPassword({ token, new_password: password }); setMessage(c.resetDone);
      }
    } catch {
      setError(c.invalid);
    } finally {
      setLoading(false);
    }
  };

  const title = mode === "forgot" ? c.forgotTitle : mode === "reset" ? c.resetTitle : c.verifyTitle;
  const body = mode === "forgot" ? c.forgotBody : mode === "verify" ? c.verifyBody : "";
  const button = mode === "forgot" ? c.send : mode === "reset" ? c.reset : token ? c.verify : c.resend;

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="card-surface w-full max-w-lg p-7 md:p-10">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <span className="w-2.5 h-2.5 bg-[var(--td-gold)] rotate-45" />
          <span className="font-serif text-xl">TradeDocs<span className="text-[var(--td-gold)]"> · </span>Desk</span>
        </Link>
        <div className="kicker mb-2">— {c.security || COPY.en.security}</div>
        <h1 className="font-serif text-4xl mb-3">{title}</h1>
        {body && <p className="text-sm text-[var(--td-text-2)] leading-relaxed mb-7">{body}</p>}
        <form onSubmit={submit} className="space-y-5">
          {((mode === "forgot") || (mode === "verify" && !token)) && (
            <div><label className="td-label">{c.email}</label><input required type="email" className="td-input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          )}
          {mode === "reset" && (
            <>
              <div><label className="td-label">{c.password}</label><input required type="password" minLength={12} className="td-input" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
              <div><label className="td-label">{c.confirm}</label><input required type="password" minLength={12} className="td-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></div>
            </>
          )}
          {message && <div className="flex gap-2 text-sm text-emerald-400"><CheckCircle2 size={18} className="shrink-0" />{message}</div>}
          {error && <div className="text-sm text-red-400">{error}</div>}
          {!message && <button disabled={loading || ((mode === "reset" || (mode === "verify" && token)) && !token)} className="btn-gold w-full inline-flex justify-center items-center gap-2 disabled:opacity-50">{loading ? c.working : button}<ArrowRight size={14} /></button>}
        </form>
        <Link to="/signin" className="block text-center text-sm text-[var(--td-text-2)] hover:text-[var(--td-gold)] mt-7">{c.back}</Link>
      </div>
    </div>
  );
}
